import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import { ReportHeader, ReportSummaryCard } from "../../components/reports";

const fmt = (n) =>
  n != null && n !== "" ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—";

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = d.toString().split("T")[0];
  const [y, m, dd] = dt.split("-");
  return `${dd}-${m}-${y}`;
};

const BUCKET_STYLES = {
  "PAR 1-30": { bg: "bg-amber-50 text-amber-700 border-amber-200", textColor: "text-amber-700" },
  "PAR 31-60": { bg: "bg-orange-50 text-orange-700 border-orange-200", textColor: "text-orange-700" },
  "PAR 61-90": { bg: "bg-rose-50 text-rose-700 border-rose-200", textColor: "text-rose-750" },
  "PAR 91-180": { bg: "bg-purple-50 text-purple-700 border-purple-200", textColor: "text-purple-700" },
  "PAR 180+": { bg: "bg-indigo-50 text-indigo-700 border-indigo-200", textColor: "text-indigo-700" },
};

const BUCKETS_ORDER = ["PAR 1-30", "PAR 31-60", "PAR 61-90", "PAR 91-180", "PAR 180+"];

const COLUMNS = [
  { key: "sno", label: "#", align: "center" },
  { key: "branchName", label: "Branch Name", align: "left" },
  { key: "branchCode", label: "Branch Code", align: "left" },
  { key: "region", label: "Region", align: "left" },
  { key: "division", label: "Division", align: "left" },
  { key: "customerName", label: "Customer Name", align: "left" },
  { key: "prospectId", label: "Prospect ID", align: "left" },
  { key: "contactNo", label: "Contact No", align: "left" },
  { key: "loanAccountNo", label: "Loan Account No", align: "left" },
  { key: "disbursedAmount", label: "Disbursed Amount", align: "right" },
  { key: "tenureInMonths", label: "Tenure", align: "center" },
  { key: "emiAmount", label: "EMI Amount", align: "right" },
  { key: "rateOfInterest", label: "ROI %", align: "center" },
  { key: "emiStartDate", label: "EMI Start Date", align: "left" },
  { key: "maturityDate", label: "Maturity Date", align: "left" },
  { key: "emiMonthsPaid", label: "EMI Paid", align: "center" },
  { key: "overdueEMIs", label: "Overdue EMIs", align: "center" },
  { key: "firstOverdueDate", label: "First Overdue Date", align: "left" },
  { key: "lastPaidDate", label: "Last Paid Date", align: "left" },
  { key: "outstandingPrincipal", label: "O/S Principal", align: "right" },
  { key: "outstandingInterest", label: "O/S Interest", align: "right" },
  { key: "overduePrincipal", label: "Overdue Principal", align: "right" },
  { key: "overdueInterest", label: "Overdue Interest", align: "right" },
  { key: "totalPAR", label: "Total PAR", align: "right" },
  { key: "principalPaid", label: "Principal Paid", align: "right" },
  { key: "interestPaid", label: "Interest Paid", align: "right" },
  { key: "closingBalance", label: "Closing Balance", align: "right" },
  { key: "dpd", label: "DPD", align: "center" },
  { key: "bucket", label: "PAR Bucket", align: "center" },
  { key: "isDemised", label: "Is Demised(Insurance)", align: "center" },
  { key: "processingFee", label: "Processing Fee", align: "right" },
  { key: "gst", label: "GST", align: "right" },
  { key: "insurance", label: "Insurance", align: "right" },
  { key: "securityDeposit", label: "Security Deposit", align: "right" },
  { key: "loanPurpose", label: "Loan Purpose", align: "left" },
  { key: "aadharNumber", label: "Aadhar Number", align: "left" },
  { key: "panNumber", label: "Pan Number", align: "left" },
  { key: "voterId", label: "Voter Id", align: "left" },
  { key: "bankAccountNumber", label: "Bank Account Number", align: "left" },
  { key: "ifscCode", label: "IFSC Code", align: "left" },
  { key: "bankName", label: "Bank Name", align: "left" },
  { key: "bankBranch", label: "Bank Branch", align: "left" },
  { key: "employeeName", label: "Employee Name", align: "left" },
  { key: "employeeCode", label: "Employee Code", align: "center" },
];

const CURRENCY_KEYS = new Set([
  "disbursedAmount", "emiAmount", "outstandingPrincipal", "outstandingInterest",
  "overduePrincipal", "overdueInterest", "totalPAR", "principalPaid", "interestPaid",
  "closingBalance", "processingFee", "gst", "insurance", "securityDeposit",
]);
const DATE_KEYS = new Set([
  "emiStartDate", "maturityDate", "firstOverdueDate", "lastPaidDate",
]);

const EMPTY_SUMMARY = {
  totalPortfolio: 0, totalPARAmount: 0, parPct: "0.00",
  parCount: 0, totalActiveLoans: 0,
  bucketSummary: Object.fromEntries(
    BUCKETS_ORDER.map((b) => [b, { count: 0, parAmount: 0, parPct: "0.00" }])
  ),
};

const PARReport = () => {
  const { data: authData, roleName } = useAuth();
  const branchId = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split("T")[0]);
  const [bucketFilter, setBucketFilter] = useState("All");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { asOfDate };
      if (!isSuperAdmin && branchId) params.branchId = branchId;

      const res = await axios.get("/api/v2/par-report", { baseURL: API_BASE_URL, params });
      let parRows = Array.isArray(res.data?.data) ? res.data.data : [];

      // Fetch member account (savings) closing balances
      if (parRows.length > 0) {
        const cuids = parRows.map((r) => r.prospectId).filter(Boolean).join(",");
        try {
          const balRes = await axios.post("/api/v2/member-closing-balances", {
            customerUniqueIds: cuids
          }, {
            baseURL: API_BASE_URL
          });
          const balMap = balRes.data?.data || {};
          parRows.forEach((r) => {
            r.closingBalance = balMap[r.prospectId] ?? r.closingBalance ?? 0;
          });
        } catch (balErr) {
          console.warn("Could not fetch member balances:", balErr);
        }
      }

      setRows(parRows);
      setSummary(res.data?.summary || EMPTY_SUMMARY);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setError("Failed to load PAR report. Please try again.");
    }
    setLoading(false);
  }, [isSuperAdmin, branchId, asOfDate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Client-side filter
  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch =
      (r.customerName || "").toLowerCase().includes(q) ||
      (r.loanAccountNo || "").toLowerCase().includes(q) ||
      (r.prospectId || "").toLowerCase().includes(q) ||
      (r.branchName || "").toLowerCase().includes(q) ||
      (r.employeeName || "").toLowerCase().includes(q) ||
      String(r.contactNo || "").includes(q);
    const matchBucket = bucketFilter === "All" || r.bucket === bucketFilter;
    return matchSearch && matchBucket;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // Filtered aggregates (recalculate for search/bucket filter)
  const filteredPAR = filtered.reduce((s, r) => s + (Number(r.totalPAR) || 0), 0);
  const filteredOS = filtered.reduce((s, r) => s + (Number(r.outstandingPrincipal) || 0), 0);

  // CSV export
  const exportCSV = () => {
    const headers = COLUMNS.map((c) => c.label);
    const csvRows = [
      headers.join(","),
      ...filtered.map((r) =>
        COLUMNS.map((c) => {
          const val = String(r[c.key] ?? "").replace(/"/g, '""');
          return val.includes(",") ? `"${val}"` : val;
        }).join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "par_report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const renderCell = (r, col, idx) => {
    const val = r[col.key];

    if (col.key === "sno") return <span className="text-ink-400">{(currentPage - 1) * rowsPerPage + idx + 1}</span>;

    if (DATE_KEYS.has(col.key)) {
      const txt = fmtDate(val);
      const isFirstOverdue = col.key === "firstOverdueDate" && val;
      return <span className={isFirstOverdue ? "text-rose-600 font-bold" : ""}>{txt}</span>;
    }

    if (CURRENCY_KEYS.has(col.key)) {
      if (val === "" || val == null) return "—";
      const isRed = ["overduePrincipal", "overdueInterest", "totalPAR"].includes(col.key);
      const isClosing = col.key === "closingBalance";
      return (
        <span className={isRed ? "text-rose-600 font-bold" : isClosing ? "text-[#0369a1] font-bold" : ""}>
          ₹{fmt(val)}
        </span>
      );
    }

    if (col.key === "dpd") {
      const colorClass = val > 180 ? "text-purple-600 font-bold" :
        val > 90 ? "text-rose-700 font-bold" :
          val > 60 ? "text-rose-600 font-bold" :
            val > 30 ? "text-orange-600 font-bold" :
              val > 0 ? "text-amber-600 font-bold" : "text-emerald-600 font-bold";
      return <span className={colorClass}>{val}</span>;
    }

    if (col.key === "bucket") {
      const s = BUCKET_STYLES[val] || { bg: "bg-ink-50 text-ink-700 border-ink-200" };
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${s.bg}`}>
          {val}
        </span>
      );
    }

    if (col.key === "customerName") return <span className="font-semibold text-ink-900">{val || "—"}</span>;
    if (col.key === "loanAccountNo") return <code className="text-xs font-mono text-primary font-bold">{val || "—"}</code>;
    if (col.key === "overdueEMIs") return <span className="text-rose-600 font-bold">{val}</span>;

    return val !== "" && val != null ? val : "—";
  };

  const bs = summary.bucketSummary || {};

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      <ReportHeader
        className="mb-4"
        filters={
          <>
            {/* As of Date */}
            <div className="w-[150px] mt-1">
              <DatePicker
                label="As of Date"
                selected={asOfDate}
                onChange={(date) => setAsOfDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* Rows/Page */}
            <div className="relative min-w-[110px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Rows/Page
              </label>
              <div className="relative">
                <select
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <div className="mt-1">
              <Button onClick={loadData} variant="secondary" className="h-[38px] px-3 font-semibold text-xs">
                Apply Filters
              </Button>
            </div>

            {/* Active Bucket Clear Button */}
            {bucketFilter !== "All" && (
              <div className="mt-1">
                <Button onClick={() => { setBucketFilter("All"); setCurrentPage(1); }} variant="secondary" className="h-[38px] text-rose-600 border-rose-200 bg-rose-50 hover:bg-rose-100 px-3 font-semibold text-xs">
                  <i className="fa-solid fa-xmark mr-1.5"></i>
                  Clear Filter: {bucketFilter}
                </Button>
              </div>
            )}

            {/* Search Input */}
            <div className="min-w-[260px] flex-grow mt-1">
              <input
                placeholder="Search customer / account / branch..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-full bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-xs px-3.5 focus:outline-none transition-all font-medium"
              />
            </div>
          </>
        }
        actions={
          <Button
            onClick={exportCSV}
            variant="primary"
            size="sm"
            className="shadow-sm"
          >
            <i className="fa-solid fa-file-csv mr-1.5"></i>
            Export CSV
          </Button>
        }
      />
      {loading && !rows.length && <Loader message="Loading PAR data..." />}

      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
        {[
          { label: "Total Portfolio", value: `₹${fmt(summary.totalPortfolio)}`, bg: "bg-[#D9F3F7]", border: "border-[#0891B2]/25", accent: "text-[#0891B2]", icon: "fa-briefcase" },
          { label: "PAR Amount (1+)", value: `₹${fmt(summary.totalPARAmount)}`, bg: "bg-[#FFF0C7]", border: "border-[#D97706]/25", accent: "text-[#D97706]", icon: "fa-triangle-exclamation" },
          { label: "PAR %", value: `${summary.parPct}%`, bg: "bg-[#E9DFFF]", border: "border-[#7C3AED]/25", accent: "text-[#7C3AED]", icon: "fa-percent" },
          { label: "PAR Accounts", value: summary.parCount, bg: "bg-[#DDF5E6]", border: "border-[#16A34A]/25", accent: "text-[#16A34A]", icon: "fa-users" },
          { label: "Active Loans", value: summary.totalActiveLoans, bg: "bg-[#DCEBFF]", border: "border-[#2563EB]/25", accent: "text-[#2563EB]", icon: "fa-file-invoice-dollar" },
        ].map((c, idx) => (
          <ReportSummaryCard key={idx} {...c} />
        ))}
      </div>

      {/* PAR Bucket Breakdown Grid */}
      <div className="bg-white py-3 px-4 rounded-xl border border-border-light shadow-sm mb-4">
        <h4 className="text-xs font-bold text-ink-850 uppercase tracking-wider mb-2.5 flex items-center gap-2">
          <i className="fa-solid fa-layer-group text-primary"></i>
          PAR Bucket Breakdown
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {BUCKETS_ORDER.map((b) => {
            const info = bs[b] || { count: 0, parAmount: 0, parPct: "0.00" };
            const style = BUCKET_STYLES[b];
            const active = bucketFilter === b;
            return (
              <div
                key={b}
                className={`py-2 px-3 rounded-lg border transition-all duration-200 flex flex-col justify-between gap-1.5 ${active ? "bg-primary/5 border-primary shadow-sm" : "bg-canvas-card border-border-light hover:brightness-98"
                  }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-extrabold uppercase tracking-wide ${style.textColor}`}>{b}</span>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${style.bg}`}>
                    {info.parPct}%
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-black text-ink-950">₹{fmt(info.parAmount)}</span>
                  <span className="text-[10px] text-ink-400 font-semibold">{info.count} Accounts</span>
                </div>
                <Button
                  onClick={() => { setBucketFilter(active ? "All" : b); setCurrentPage(1); }}
                  variant={active ? "primary" : "secondary"}
                  size="xs"
                  className="w-full text-center py-1 font-bold h-[28px] text-[11px] rounded"
                >
                  {active ? "Clear Filter" : "Filter Bucket"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filtered totals */}
      {(search || bucketFilter !== "All") && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {[
            { label: `Filtered Accounts (${bucketFilter !== "All" ? bucketFilter : "Search"})`, value: filtered.length, color: "text-purple-600 bg-purple-50/20" },
            { label: "Filtered PAR", value: `₹${fmt(filteredPAR)}`, color: "text-rose-600 bg-rose-50/20" },
            { label: "Filtered O/S", value: `₹${fmt(filteredOS)}`, color: "text-orange-650 bg-orange-50/20" },
          ].map((c, idx) => (
            <div key={idx} className="bg-white p-4 rounded-2xl border border-border-light shadow-sm flex flex-col justify-between">
              <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider mb-1">{c.label}</span>
              <span className={`text-base font-black ${c.color.split(' ')[0]}`}>{c.value}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="bg-rose-50 text-rose-700 p-4 rounded-xl border border-rose-200 text-sm font-medium mb-4">
          {error}
        </div>
      )}

      {/* Main Table */}
      <div className="flex-grow flex flex-col min-h-0">
        <Table className="min-w-[2800px]">
          <Table.Head>
            <tr>
              {COLUMNS.map((c) => (
                <Table.HeadCell key={c.key} align={c.align} className="whitespace-nowrap">
                  {c.label}
                </Table.HeadCell>
              ))}
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <Table.Row>
                <Table.Cell colSpan={COLUMNS.length} align="center" className="py-10">
                  <div className="flex flex-col items-center justify-center py-4">
                    <Loader size="md" />
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : paginated.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={COLUMNS.length} align="center" className="py-12">
                  <div className="flex flex-col items-center justify-center gap-2 text-emerald-600">
                    <i className="fa-solid fa-circle-check text-3xl"></i>
                    <span className="font-bold text-sm">No PAR records found for the selected criteria.</span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : (
              paginated.map((r, i) => {
                const bStyle = BUCKET_STYLES[r.bucket] || {};
                const isOdd = i % 2 !== 0;
                return (
                  <Table.Row key={i} className={isOdd ? "bg-purple-50/5" : ""}>
                    {COLUMNS.map((col) => (
                      <Table.Cell key={col.key} align={col.align} className="whitespace-nowrap">
                        {renderCell(r, col, i)}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between mt-4 bg-white p-3 rounded-xl border border-border-light shadow-sm">
            <span className="text-xs text-ink-500">
              Showing {paginated.length} of {filtered.length} records {filtered.length !== rows.length && `(filtered from ${rows.length})`}
            </span>
            <div className="flex gap-1 items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-xs text-ink-500 px-3 font-semibold">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default PARReport;
